import { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface DateRangeData {
    start: string | null;
    end: string | null;
    flexible: boolean;
}

interface DateRangePickerProps {
    value: DateRangeData;
    onChange: (data: DateRangeData) => void;
    isExpanded?: boolean;
}

type QuickOption = 'today' | 'tomorrow' | 'weekend' | 'flexible' | null;

// Convert Date to YYYY-MM-DD string in local timezone (not UTC)
function toLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function DateRangePicker({ value, onChange, isExpanded = false }: DateRangePickerProps) {
    const { t, i18n } = useTranslation();
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [selectingEnd, setSelectingEnd] = useState(false);
    const isNavigatingRef = useRef(false);

    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    // Get quick option dates
    const getQuickDates = useMemo(() => {
        const todayStr = toLocalDateString(today);
        
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = toLocalDateString(tomorrow);

        // Get this weekend (Saturday and Sunday)
        const dayOfWeek = today.getDay();
        const daysUntilSaturday = dayOfWeek === 0 ? 6 : 6 - dayOfWeek;
        const saturday = new Date(today);
        saturday.setDate(saturday.getDate() + daysUntilSaturday);
        const sunday = new Date(saturday);
        sunday.setDate(sunday.getDate() + 1);
        
        return {
            today: { start: todayStr, end: todayStr },
            tomorrow: { start: tomorrowStr, end: tomorrowStr },
            weekend: { 
                start: toLocalDateString(saturday), 
                end: toLocalDateString(sunday) 
            },
        };
    }, [today]);

    // Detect active quick option
    const activeQuickOption = useMemo((): QuickOption => {
        if (value.flexible) return 'flexible';
        if (!value.start || !value.end) return null;
        
        const { today: todayDates, tomorrow: tomorrowDates, weekend: weekendDates } = getQuickDates;
        
        if (value.start === todayDates.start && value.end === todayDates.end) return 'today';
        if (value.start === tomorrowDates.start && value.end === tomorrowDates.end) return 'tomorrow';
        if (value.start === weekendDates.start && value.end === weekendDates.end) return 'weekend';
        
        return null;
    }, [value, getQuickDates]);

    // Format date for quick option display
    const formatQuickDate = (dateStr: string) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString(i18n.language === 'ro' ? 'ro-RO' : 'en-US', { 
            month: 'short', 
            day: 'numeric' 
        });
    };

    // Handle quick option selection
    const handleQuickOption = (option: QuickOption) => {
        if (option === 'flexible') {
            onChange({ start: null, end: null, flexible: true });
        } else if (option) {
            const dates = getQuickDates[option];
            onChange({ start: dates.start, end: dates.end, flexible: false });
        }
    };

    // Calendar logic
    const calendarDays = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        const days: { date: Date | null; day: number }[] = [];
        
        // Add empty days for padding (start on Sunday)
        const startPadding = firstDay.getDay();
        for (let i = 0; i < startPadding; i++) {
            days.push({ date: null, day: 0 });
        }
        
        // Add month days
        for (let d = 1; d <= lastDay.getDate(); d++) {
            days.push({ date: new Date(year, month, d), day: d });
        }
        
        return days;
    }, [currentMonth]);

    const monthTitle = useMemo(() => {
        return currentMonth.toLocaleDateString(i18n.language === 'ro' ? 'ro-RO' : 'en-US', { 
            month: 'long', 
            year: 'numeric' 
        });
    }, [currentMonth, i18n.language]);

    const weekdays = useMemo(() => {
        const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
        if (i18n.language === 'ro') {
            return ['D', 'L', 'Ma', 'Mi', 'J', 'V', 'S'];
        }
        return days;
    }, [i18n.language]);

    const handleDayClick = (date: Date) => {
        const dateStr = toLocalDateString(date);
        
        if (!value.start || selectingEnd === false) {
            // Start a new selection
            onChange({ start: dateStr, end: null, flexible: false });
            setSelectingEnd(true);
        } else {
            // Complete the selection
            const startDate = new Date(value.start);
            if (date < startDate) {
                // If clicked date is before start, swap them
                onChange({ start: dateStr, end: value.start, flexible: false });
            } else {
                onChange({ start: value.start, end: dateStr, flexible: false });
            }
            setSelectingEnd(false);
        }
    };

    const isDayDisabled = (date: Date) => {
        return date < today;
    };

    const isDaySelected = (date: Date) => {
        const dateStr = toLocalDateString(date);
        return value.start === dateStr || value.end === dateStr;
    };

    const isDayInRange = (date: Date) => {
        if (!value.start || !value.end) return false;
        const startDate = new Date(value.start);
        const endDate = new Date(value.end);
        return date > startDate && date < endDate;
    };

    const isDayRangeStart = (date: Date) => {
        if (!value.start) return false;
        return toLocalDateString(date) === value.start;
    };

    const isDayRangeEnd = (date: Date) => {
        if (!value.end) return false;
        return toLocalDateString(date) === value.end;
    };

    const isDayToday = (date: Date) => {
        return date.toDateString() === today.toDateString();
    };

    const navigateMonth = (delta: number, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Prevent double navigation with longer debounce
        if (isNavigatingRef.current) return;
        isNavigatingRef.current = true;
        
        // Navigate by changing year/month only; use day 1 to avoid overflow (e.g. Jan 31 + 1 month → Mar 2)
        setCurrentMonth(prev => {
            return new Date(prev.getFullYear(), prev.getMonth() + delta, 1);
        });
        
        // Reset flag after animation completes
        setTimeout(() => {
            isNavigatingRef.current = false;
        }, 300);
    };

    const canNavigateBack = useMemo(() => {
        const prevMonthFirst = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
        const thisMonthFirst = new Date(today.getFullYear(), today.getMonth(), 1);
        return prevMonthFirst >= thisMonthFirst;
    }, [currentMonth, today]);

    if (!isExpanded) {
        return null;
    }

    return (
        <div className="date-picker-container">
            {/* Quick Options */}
            <div className="date-quick-options">
                <button
                    type="button"
                    className={`date-quick-btn ${activeQuickOption === 'today' ? 'active' : ''}`}
                    onClick={() => handleQuickOption('today')}
                >
                    <span className="date-quick-title">{t('tripBuilder.date_today', 'Today')}</span>
                    <span className="date-quick-subtitle">{formatQuickDate(getQuickDates.today.start)}</span>
                </button>

                <button
                    type="button"
                    className={`date-quick-btn ${activeQuickOption === 'tomorrow' ? 'active' : ''}`}
                    onClick={() => handleQuickOption('tomorrow')}
                >
                    <span className="date-quick-title">{t('tripBuilder.date_tomorrow', 'Tomorrow')}</span>
                    <span className="date-quick-subtitle">{formatQuickDate(getQuickDates.tomorrow.start)}</span>
                </button>

                <button
                    type="button"
                    className={`date-quick-btn ${activeQuickOption === 'weekend' ? 'active' : ''}`}
                    onClick={() => handleQuickOption('weekend')}
                >
                    <span className="date-quick-title">{t('tripBuilder.date_weekend', 'This weekend')}</span>
                    <span className="date-quick-subtitle">
                        {formatQuickDate(getQuickDates.weekend.start)} – {formatQuickDate(getQuickDates.weekend.end)}
                    </span>
                </button>

                <button
                    type="button"
                    className={`date-quick-btn ${activeQuickOption === 'flexible' ? 'active' : ''}`}
                    onClick={() => handleQuickOption('flexible')}
                >
                    <span className="date-quick-title">{t('tripBuilder.date_flexible_short', 'Flexible')}</span>
                    <span className="date-quick-subtitle">{t('tripBuilder.date_flexible_desc', 'Any dates')}</span>
                </button>
            </div>

            {/* Calendar */}
            <div className="date-calendar-section">
                <div className="calendar-header">
                    <button
                        type="button"
                        className="calendar-nav-btn"
                        onClick={(e) => navigateMonth(-1, e)}
                        disabled={!canNavigateBack}
                        aria-label={t('tripBuilder.prev_month', 'Previous month')}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>
                    
                    <span className="calendar-month-title">{monthTitle}</span>
                    
                    <button
                        type="button"
                        className="calendar-nav-btn"
                        onClick={(e) => navigateMonth(1, e)}
                        aria-label={t('tripBuilder.next_month', 'Next month')}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                </div>

                <div className="calendar-weekdays">
                    {weekdays.map((day, i) => (
                        <span key={i} className="calendar-weekday">{day}</span>
                    ))}
                </div>

                <div className="calendar-days">
                    {calendarDays.map((item, i) => {
                        if (!item.date) {
                            return <span key={i} className="calendar-day empty" />;
                        }
                        
                        const date = item.date;
                        const disabled = isDayDisabled(date);
                        const selected = isDaySelected(date);
                        const inRange = isDayInRange(date);
                        const rangeStart = isDayRangeStart(date);
                        const rangeEnd = isDayRangeEnd(date);
                        const isToday = isDayToday(date);
                        
                        let className = 'calendar-day';
                        if (selected) className += ' selected';
                        if (inRange) className += ' in-range';
                        if (rangeStart && value.end) className += ' range-start';
                        if (rangeEnd && value.start) className += ' range-end';
                        if (isToday) className += ' today';
                        
                        return (
                            <button
                                key={i}
                                type="button"
                                className={className}
                                disabled={disabled}
                                onClick={() => handleDayClick(date)}
                            >
                                {item.day}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
